
  import { computed, defineComponent, inject, Ref, ref, watch } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { InputField } from '@/ui/components/form/InputField';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import { du } from '@/ui/i18n/i18n';
  import { Tag } from '@/ui/components/Tag';
  import { openNewRouterTab, toFixed } from '@/ui/helpers';
  import StaticPagination from '@/ui/components/StaticPagination.vue';
  import { useDetailedView, useDetailedViewFilters } from '@/ui/concerns/analytics/useDetailedView';
  import { useDetailedViewStore } from '@/stores/analytics/detailedView';
  import { ReportRecordData } from '@/services/api/resources/analytics-builder/model';
  import { reportFromAnalyticsPageKey } from '@/ui/injectionKeys';

  export default defineComponent({
    components: {
      Loader,
      Box,
      InputField,
      ResourceTable,
      Tag,
      Pagination: StaticPagination,
    },
    setup() {
      const { getDetailedViewDataTask, data, search, page, perPage } = useDetailedView();
      const { prepareFilters } = useDetailedViewFilters();
      const store = useDetailedViewStore();
      const report = inject<Ref<ReportRecordData | null>>(reportFromAnalyticsPageKey, ref(null));

      const widget = computed(() => report.value?.widgets.find(widget => widget.key === store.widgetKey));
      const combinedFilters = computed(() => prepareFilters(
        widget.value!.filters,
        report.value!.filters,
        widget.value!.attribute,
        store.resourceId!,
        store.options,
      ));

      const taskParams = computed(() => ({
        filters: combinedFilters.value,
        metric: widget.value!.metric,
        attribute: widget.value!.attribute,
        page: page.value,
        perPage,
        searchTerm: search.value,
      }));

      const onPageChanged = (value: number) => {
        page.value = value;
      };

      const updateSearch = (value: string) => {
        search.value = value;
      };

      watch([page, search], () => getDetailedViewDataTask.value.run(taskParams.value));

      const onRowClick = ({ id }: { id: string }) => {
        openNewRouterTab({ name: 'inspectionEdit', params: { id } });
      };

      const getAssignees = (item: { assignees: Array<{ value: string }>; user: { value: string } }) => [
        ...item.assignees.map(group => group.value),
      ].join(', ');

      return {
        search,
        page,
        perPage,
        onPageChanged,
        updateSearch,
        getDetailedViewDataTask,
        taskParams,
        data,
        getAssignees,
        toFixed,
        du,
        onRowClick,
      };
    },
  });
